import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import './VendorResetPassword.css';
const FormMessageDisplay = ({ message, type }) => {
    if (!message) return null;
    const baseStyle = {
        padding: '12px 16px', borderRadius: '8px', fontSize: '0.9rem',
        fontWeight: '500', marginBottom: '20px', textAlign: 'left',
        border: '1px solid transparent'
    };
    const styleMap = {
        error: { ...baseStyle, backgroundColor: '#fee2e2', color: '#ef4444', borderColor: '#fca5a5' },
        success: { ...baseStyle, backgroundColor: '#e6f9f3', color: '#0bc184', borderColor: '#a7f3d0' },
        info: { ...baseStyle, backgroundColor: '#eff6ff', color: '#25a5f0', borderColor: '#bfdbfe' }
    };
    return (
        <div 
            className="form-message-alert"
            style={styleMap[type] || styleMap.info} 
            role="alert" 
            dangerouslySetInnerHTML={{ __html: message }} 
        />
    );
};


function ResetPassword() {
    // --- State Variables ---
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [token, setToken] = useState(null);
    // Re-introduce formMessage state
    const [formMessage, setFormMessage] = useState({ text: '', type: '' });
    const [isLoading, setIsLoading] = useState(false);

    // --- Hooks ---
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // --- Effects ---
    useEffect(() => {
        document.title = 'Reset Password';

        const urlToken = searchParams.get('token');
        if (urlToken) {
            setToken(urlToken);
        } else {
            // Use setFormMessage for missing token error
            setFormMessage({
                text: "Password reset token not found in URL. Please use the link from your email.",
                type: 'error'
            });
        }

    }, [searchParams]);

    // --- Event Handlers ---
    const handleResetSubmit = async (e) => {
        e.preventDefault();
        setFormMessage({ text: '', type: '' }); // Clear previous messages

        if (!token) {
            setFormMessage({ text: "Cannot reset password without a valid token.", type: 'error' });
            return;
        }
        if (!newPassword || !confirmPassword) {
            setFormMessage({ text: "Please enter and confirm your new password.", type: 'error' });
            return;
        }
        if (newPassword !== confirmPassword) {
            setFormMessage({ text: "Passwords do not match.", type: 'error' });
            return;
        }
        if (newPassword.length < 8) {
            setFormMessage({ text: "Password must be at least 8 characters long.", type: 'error' });
            return;
        }

        setIsLoading(true);
        setFormMessage({ text: 'Processing your request...', type: 'info' }); // Optional: show processing message

        try {
            const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/vendors/reset-password`;
            const response = await axios.post(apiUrl, {
                token: token,
                newPassword: newPassword,
                confirmPassword: confirmPassword
            });

            const successMessage = response.data.message || "Password has been reset successfully! Redirecting to login...";
            setFormMessage({ text: successMessage, type: 'success' });

            setNewPassword('');
            setConfirmPassword('');

            // Redirect after a short delay to allow user to see the success message
            setTimeout(() => {
                navigate('/login');
            }, 3000); // 3 seconds delay

        } catch (err) {
            console.error("Password reset failed:", err);
            const errorMessage = err.response?.data?.message || "Failed to reset password. The token might be invalid, expired, or there was a server error.";
            setFormMessage({ text: errorMessage, type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <main className="reset-password-page">
                <div className="reset-password-wrapper">
                    <div className="reset-password-logo">
                        <Link to="/"><img src="/logo-mangoloops.png" alt="MangoLoops Logo" /></Link>
                    </div>
                    <section className="reset-password-card">
                        <div className="reset-password-header">
                            <h2>Set New Password</h2>
                            <p>Enter and confirm your new password below.</p>
                        </div>
                            <FormMessageDisplay message={formMessage.text} type={formMessage.type} />
                        {token && (
                            <form onSubmit={handleResetSubmit} noValidate>
                                <div className="reset-password-field">
                                    <label htmlFor="new-password">New Password</label>
                                    <div className="reset-password-input-wrapper">
                                        <input
                                                id="new-password"
                                                type={showNewPassword ? 'text' : 'password'}
                                                placeholder="Enter new password"
                                                value={newPassword}
                                                onChange={(e) => { setFormMessage({ text: '', type: '' }); setNewPassword(e.target.value); }}
                                                required
                                                disabled={isLoading}
                                        />
                                        <button type="button" className="reset-password-toggle" onClick={() => setShowNewPassword((visible) => !visible)} aria-label={showNewPassword ? 'Hide new password' : 'Show new password'} aria-pressed={showNewPassword} disabled={isLoading}>
                                            {showNewPassword ? <FiEyeOff size={20} aria-hidden="true" /> : <FiEye size={20} aria-hidden="true" />}
                                        </button>
                                    </div>
                                </div>
                                <div className="reset-password-field">
                                    <label htmlFor="confirm-password">Confirm Password</label>
                                    <div className="reset-password-input-wrapper">
                                        <input
                                                id="confirm-password"
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                placeholder="Confirm new password"
                                                value={confirmPassword}
                                                onChange={(e) => { setFormMessage({ text: '', type: '' }); setConfirmPassword(e.target.value); }}
                                                required
                                                disabled={isLoading}
                                        />
                                        <button type="button" className="reset-password-toggle" onClick={() => setShowConfirmPassword((visible) => !visible)} aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'} aria-pressed={showConfirmPassword} disabled={isLoading}>
                                            {showConfirmPassword ? <FiEyeOff size={20} aria-hidden="true" /> : <FiEye size={20} aria-hidden="true" />}
                                        </button>
                                    </div>
                                </div>
                                <button type="submit" className="reset-password-button" disabled={isLoading || formMessage.type === 'success'}>
                                    {isLoading ? 'Resetting Password...' : 'Reset Password'}
                                </button>
                            </form>
                        )}
                        {!token && !formMessage.text && <p>Loading token information...</p>}
                        <div className="reset-password-footer">
                            Remembered your password? <Link to="/login">Sign in</Link>
                        </div>
                    </section>
                </div>
            </main>
        </>
    );
}

export default ResetPassword;
